import { useState, useMemo, useEffect } from "react";

interface PaginationResult<T> {
  page: number;
  rowsPerPage: number;
  totalRecords: number;
  totalPages: number;
  startIdx: number;
  endIdx: number;
  paginatedItems: T[];
  setPage: (page: number) => void;
  setRowsPerPage: (size: number) => void;
  handlePrevPage: () => void;
  handleNextPage: () => void;
  handleRowsPerPageChange: (val: string | null) => void;
}

/**
 * Reusable pagination hook for table components.
 *
 * @example
 * const { paginatedItems, page, totalPages, handlePrevPage, handleNextPage } = usePagination(records, 10);
 */
const usePagination = <T>(
  items: T[],
  initialPageSize = 10
): PaginationResult<T> => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);

  // Reset to page 1 when items change
  useEffect(() => {
    setPage(1);
  }, [items]);

  const totalRecords = items.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const startIdx = (page - 1) * rowsPerPage;
  const endIdx = Math.min(startIdx + rowsPerPage, totalRecords);

  const paginatedItems = useMemo(
    () => items.slice(startIdx, endIdx),
    [items, startIdx, endIdx]
  );

  const handlePrevPage = (): void => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = (): void => {
    if (page < totalPages) setPage(page + 1);
  };

  const handleRowsPerPageChange = (val: string | null): void => {
    setRowsPerPage(parseInt(val || String(initialPageSize), 10));
    setPage(1);
  };

  return {
    page,
    rowsPerPage,
    totalRecords,
    totalPages,
    startIdx,
    endIdx,
    paginatedItems,
    setPage,
    setRowsPerPage,
    handlePrevPage,
    handleNextPage,
    handleRowsPerPageChange,
  };
};

export default usePagination;
